// Library Methods
import i18next from "i18next";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import { SelectChangeEvent } from "@mui/material/Select";
import { useTheme } from "@mui/material/styles";
import DefaultCard from "src/components/shared/layouts/DefaultCard/DefaultCard";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import CustomSwitch from "src/components/shared/inputs/CustomSwitch/CustomSwitch";
import ReportLayoutSetup from "src/components/containers/Dashboard/WelcomeModal/ReportLayoutSetup";
import ReportOrderRadio from "src/components/containers/shared/ReportOrderRadio/ReportOrderRadio";
import Alert from "src/components/shared/alerts/Alert/Alert";
import downloadIconImg from "src/assets/images/clinic-settings/download-icon.png";
import downloadIconFrImg from "src/assets/images/clinic-settings/download-icon-fr.png";

// Styles
import { UI_SIZES } from "src/constants/uiTokens";
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Types
import { UpdateClinicSettingsType } from "src/services/clinics/types";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Config
import { priorityLanguageMap } from "src/config/languages";

// Constants
import { ONTARIO_TIMEZONES } from "src/constants/enums";

type ReportSettingsType = {
  isLoading: boolean;
  isUpdating: boolean;
  currentSettings: UpdateClinicSettingsType;
  handleUpdateSettings: (settings: UpdateClinicSettingsType) => void;
};

const getSwitchLabelSx = (mutedText: string) => ({
  marginRight: 0,
  "& .MuiFormControlLabel-label": {
    fontSize: "14px",
    marginLeft: "0px",
    color: mutedText,
    "&.Mui-disabled": { color: mutedText },
  },
});

const ReportSettings = ({
  isLoading,
  isUpdating,
  currentSettings,
  handleUpdateSettings,
}: ReportSettingsType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText } = getSurfaceTokens(theme);
  const { clinicInfo } = useClinicContext();
  const currentLang = i18next.language as keyof typeof priorityLanguageMap;

  const reportFileDropdownOptions = useMemo(() => {
    const options = [{ displayName: t("PDF"), value: "PDF" }];

    if (clinicInfo?.licensing?.canUseDicomReports) {
      options.push({ displayName: t("DICOM"), value: "DICOM" });
    }
    if (clinicInfo?.licensing?.canUsePngReports) {
      options.push({ displayName: t("PNG"), value: "PNG" });
    }

    return options;
  }, [
    t,
    clinicInfo?.licensing?.canUseDicomReports,
    clinicInfo?.licensing?.canUsePngReports,
  ]);

  const handleSetDefaultReportType = useCallback(
    (event: SelectChangeEvent<any>) => {
      const value = reportFileDropdownOptions.find(
        (option) => option.displayName === event.target.value
      )?.value;

      if (value) {
        handleUpdateSettings({
          ...currentSettings,
          defaultReportType: value,
        });
      }
    },
    [reportFileDropdownOptions, currentSettings, handleUpdateSettings]
  );

  const MTOFixationMethod = useMemo(
    () => Boolean(currentSettings?.MTOFixationMethod),
    [currentSettings?.MTOFixationMethod]
  );

  const handleUpdateMTOSwitch = useCallback(
    (inputValue: boolean) => {
      handleUpdateSettings({
        ...currentSettings,
        MTOFixationMethod: inputValue,
      });
    },
    [handleUpdateSettings, currentSettings]
  );

  const skeletonInput = (
    <Skeleton
      variant="rounded"
      width="100%"
      height={UI_SIZES.inputHeight}
      sx={{ borderRadius: "4px" }}
    />
  );

  return (
    <Box display="flex" flexWrap="wrap" gap="16px" width="100%">
      <Box sx={{ flex: "1 1 400px", minWidth: 0 }}>
        <DefaultCard>
          {/* Left column: Monocular PDF Layout + Default Report Type */}
          <Box display="flex" flexDirection="column" gap="16px">
            {/* Monocular Report PDF Layout */}
            <ReportLayoutSetup />
          </Box>
        </DefaultCard>
      </Box>

      <Box sx={{ flex: "1 1 400px", minWidth: 0 }}>
        <DefaultCard>
          {/* Right column: Report Eye Display Order + MTO Report Configuration */}
          <Box display="flex" flexDirection="column" gap="16px">
            {/* Default Report Type */}
            <Box display="flex" flexDirection="column" gap="6px">
              <Typography fontSize="14px" fontWeight={600}>
                {t("Default Report Type")}
              </Typography>
              <Typography fontSize="14px" color={mutedText} mb="10px">
                {t(
                  "Controls which file type is used for reports downloaded from the Dashboard or Patient pages."
                )}
              </Typography>
              <img
                src={currentLang === "fr" ? downloadIconFrImg : downloadIconImg}
                width="100%"
                alt="download-icon"
              />
              {isLoading ? (
                skeletonInput
              ) : (
                <SelectDropdown
                  id="report-settings-default-type-dropdown"
                  disabled={isUpdating}
                  dropdownOptions={reportFileDropdownOptions}
                  value={t(currentSettings.defaultReportType)}
                  handleChange={handleSetDefaultReportType}
                />
              )}
            </Box>
            {/* Report Eye Display Order */}
            <Box display="flex" flexDirection="column" gap="6px">
              <TitleWithTooltip
                title={t("Report Eye Display Order")}
                tooltip={t(
                  "Controls which eye is displayed first on monocular report PDFs."
                )}
                titleStyle={{ fontSize: "14px", fontWeight: 600 }}
              />
              <ReportOrderRadio
                isDisabled={isUpdating || isLoading}
                currentSettings={currentSettings}
                handleUpdateSettings={handleUpdateSettings}
              />
            </Box>

            {/* MTO Report Configuration */}
            {ONTARIO_TIMEZONES.includes(clinicInfo?.timezone ?? "") && (
              <Box display="flex" flexDirection="column" gap="6px">
                <TitleWithTooltip
                  title={t("MTO Report Configuration")}
                  tooltip={t(
                    "Displays exam reports in the format required by the Ontario Ministry of Transportation (MTO)."
                  )}
                  titleStyle={{ fontSize: "14px", fontWeight: 600 }}
                />
                <Alert
                  variant="warning"
                  alertContent={
                    <Typography fontSize="14px">
                      {t(
                        "Only enable this setting if your clinic is based in Ontario."
                      )}
                    </Typography>
                  }
                />
                <FormControlLabel
                  sx={getSwitchLabelSx(mutedText)}
                  control={
                    <CustomSwitch
                      disabled={isLoading || isUpdating}
                      checked={MTOFixationMethod}
                      onChange={(e) => handleUpdateMTOSwitch(e.target.checked)}
                      sx={{
                        ...(theme.palette.mode === "dark" && {
                          "& .MuiSwitch-track": {
                            backgroundColor: "#202338",
                          },
                          "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track":
                            {
                              backgroundColor: "#20233843",
                            },
                        }),
                      }}
                    />
                  }
                  label={t("Enable MTO format for Esterman exams")}
                />
              </Box>
            )}
          </Box>
        </DefaultCard>
      </Box>
    </Box>
  );
};

export default ReportSettings;
